import React from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldTitle,
  RadioGroup,
  RadioGroupItem,
  fieldGroupVariants,
} from '@column/ui';

import { EntityFormType } from '../_util/useEntityForm';

const ENTITY_TYPE_OPTIONS: { value: EntityFormType; label: string; description: string; icon: React.ReactNode }[] = [
  { value: EntityFormType.Person, label: 'Person', description: 'An individual person.', icon: <Icon.User /> },
  {
    value: EntityFormType.Business,
    label: 'Business',
    description: 'A company or organization.',
    icon: <Icon.Business />,
  },
];

// Entity type control. While creating, it's two selectable cards — an icon to the left of a title
// and a short description, with the radio vertically centered on the right. Once the entity exists
// the type is fixed, so it collapses to a single full-width, non-interactive summary of the chosen
// type (no radio, no divider) rather than a disabled picker.
export const EntityTypeSelector: React.FC<{
  type?: EntityFormType;
  isReadOnly: boolean;
  onSelect: (next: EntityFormType) => void;
}> = ({ type, isReadOnly, onSelect }) => {
  if (isReadOnly) {
    const selected = ENTITY_TYPE_OPTIONS.find((option) => option.value === type) ?? ENTITY_TYPE_OPTIONS[0];
    return (
      <div className={fieldGroupVariants({ variant: 'outline' })}>
        <div className="flex items-center gap-3 p-4">
          <span className="text-gray-400">{selected.icon}</span>
          <div className="flex flex-col gap-0.5">
            <FieldTitle>{selected.label}</FieldTitle>
            <FieldDescription className="text-xs">{selected.description}</FieldDescription>
          </div>
        </div>
      </div>
    );
  }

  return (
    <RadioGroup
      className={fieldGroupVariants({ variant: 'outline', columns: 2 })}
      value={type}
      onValueChange={(next) => next && onSelect(next as EntityFormType)}
    >
      {ENTITY_TYPE_OPTIONS.map((option) => {
        const optionId = `entity-type-${option.value}`;
        return (
          <Field key={option.value} orientation="vertical">
            <div className="flex items-center gap-3">
              <span className="text-gray-400">{option.icon}</span>
              <div className="flex flex-1 flex-col gap-0.5">
                <FieldLabel htmlFor={optionId} className="font-medium">
                  {option.label}
                </FieldLabel>
                <FieldDescription className="text-xs">{option.description}</FieldDescription>
              </div>
              <RadioGroupItem value={option.value} id={optionId} className="shrink-0" />
            </div>
          </Field>
        );
      })}
    </RadioGroup>
  );
};
